import { useState } from 'react';
import { API_CONFIG, HTTP_METHODS } from '../config/api';
import { useAuth } from './useAuth';

// Custom hook for cart API calls
export const useCartApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getValidToken } = useAuth();

  // Generic API call function with safe JSON parsing
  const makeApiCall = async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const {
        method = HTTP_METHODS.GET,
        headers = {},
        body = null,
        baseUrl = API_CONFIG.BASE_URL,
      } = options;

      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');

      // Prepare headers
      const requestHeaders = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...headers,
      };

      // Prepare request config
      const config = {
        method,
        headers: requestHeaders,
      };

      // Add body for POST/PUT requests
      if (body && (method === HTTP_METHODS.POST || method === HTTP_METHODS.PUT)) {
        config.body = JSON.stringify(body);
      }

      const response = await fetch(`${baseUrl}${endpoint}`, config);
      
      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return { success: true, data: data || {} };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        setError(errorMessage);
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Add to Cart API call
  const addToCartApi = async (customerId, productVariantId, quantity) => {
    const result = await makeApiCall(API_CONFIG.CART.ADD, {
      method: HTTP_METHODS.POST,
      body: {
        customerId,
        productVariantId,
        quantity
      }
    });

    if (result.success) {
      return {
        success: true,
        cartItemId: result.data.cartItemId,
        message: result.data.message || 'Product added to cart',
        data: result.data
      };
    }

    return result;
  };

  // Get Cart Items API call
  const getCartItemsApi = async (customerId) => {
    const result = await makeApiCall(`${API_CONFIG.CART.GET}/${customerId}`, {
      method: HTTP_METHODS.GET
    });

    if (result.success) {
      return {
        success: true,
        cartItems: result.data,
        data: result.data
      };
    }

    return result;
  };

  // Update Cart Quantity API call (without global loading state)
  const updateCartQuantityApi = async (cartItemId, quantity) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CART.UPDATE_QUANTITY}`, {
        method: HTTP_METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          cartItemId,
          quantity
        })
      });

      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return {
          success: true,
          message: data?.message || 'Cart updated successfully',
          data: data
        };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      return { success: false, message: errorMessage };
    }
  };

  // Remove from Cart API call (without global loading state)
  const removeFromCartApi = async (cartItemId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CART.REMOVE}/${cartItemId}`, {
        method: HTTP_METHODS.DELETE,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return {
          success: true,
          message: data?.message || 'Item removed from cart',
          data: data
        };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      return { success: false, message: errorMessage };
    }
  };

  // Clear Cart API call
  const clearCartApi = async (customerId) => {
    const result = await makeApiCall(`${API_CONFIG.CART.CLEAR}/${customerId}`, {
      method: HTTP_METHODS.DELETE
    });

    if (result.success) {
      return {
        success: true,
        message: result.data.message || 'Cart cleared successfully',
        data: result.data
      };
    }

    return result;
  };

  // Checkout Cart API call
  const checkoutCartApi = async (customerId) => {
    const result = await makeApiCall(`${API_CONFIG.CART.CHECKOUT}/${customerId}`, {
      method: HTTP_METHODS.POST
    });

    if (result.success) {
      return {
        success: true,
        checkoutData: result.data,
        data: result.data
      };
    }

    return result;
  };

  return {
    loading,
    error,
    addToCartApi,
    getCartItemsApi,
    updateCartQuantityApi,
    removeFromCartApi,
    clearCartApi,
    checkoutCartApi,
    makeApiCall
  };
};